import React, { useEffect, useState } from 'react';
import { Table, Button, Select, Input, Spin, Dropdown, Menu, Modal, Tooltip } from 'antd';
import ExcelJS from 'exceljs';
import moment from 'moment';
import './index.css';
import { reqGetListKey, reqGetKMSOrganizationUsersActiveWithLicenseExpiredDate } from '../../services';
import { ListKey, UserInfo, KMSUserWithLicenseExpired } from '../../constant/types';
import UserProfile from '../../component/UserProfile';

const { Option } = Select;

interface CustomerData {
    id: string;
    user_id: number;
    sign_in_provider: string;
    phone_number?: string;
    email?: string;
    external_user_id?: string;
    license_expired_date?: string;
    activation_count: number;
    last_activation?: number;
}

const CustomerManagement: React.FC = () => {
    const [customers, setCustomers] = useState<CustomerData[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [sortField, setSortField] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [searchText, setSearchText] = useState('');
    const [providerFilter, setProviderFilter] = useState<string>('all');
    const [expiryMonths, setExpiryMonths] = useState<number | null>(null);
    const [expiryTimeUnit, setExpiryTimeUnit] = useState<'week' | 'month' | 'year'>('month');
    const [filterType, setFilterType] = useState<'all' | 'expired' | 'custom'>('all');
    const [modalVisible, setModalVisible] = useState(false);
    const [modalContent, setModalContent] = useState<{ title: string; content: string }>({ title: '', content: '' });

    // Helper function để tính số ngày còn lại từ license_expired_date
    const getDaysRemaining = (licenseExpiredDate?: string): number => {
        if (!licenseExpiredDate) return 0;
        const expiryTimestamp = parseInt(licenseExpiredDate) * 1000;
        const now = new Date().getTime();
        const diffTime = expiryTimestamp - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    };

    // Helper function để format thời gian linh hoạt (năm, tháng, ngày)
    const formatTimeRemaining = (days: number): string => {
        if (days <= 0) return 'Đã hết hạn';
        
        const years = Math.floor(days / 365);
        const remainingDaysAfterYears = days % 365;
        const months = Math.floor(remainingDaysAfterYears / 30);
        const remainingDays = remainingDaysAfterYears % 30;
        
        const parts: string[] = [];
        
        if (years > 0) {
            parts.push(`${years} năm`);
        }
        
        if (months > 0) {
            parts.push(`${months} tháng`);
        }
        
        if (remainingDays > 0 || parts.length === 0) {
            parts.push(`${remainingDays} ngày`);
        }
        
        return `Còn ${parts.join(' ')}`;
    };

    // Function để show popup với thông tin đầy đủ
    const showInfoModal = (title: string, content: string) => {
        setModalContent({ title, content });
        setModalVisible(true);
    };

    // Fetch dữ liệu khách hàng từ API
    const fetchCustomers = async (page = 1, size = 20, sort?: string, order?: 'asc' | 'desc') => {
        setLoading(true);
        try {
            // Chuẩn bị params cho API - bắt buộc có size và page
            const apiParams: {
                expired_in?: number;
                unit?: 'week' | 'month' | 'year';
                size: number;
                page: number;
            } = {
                size: size,
                page: page
            };
            
            if (filterType === 'expired') {
                // Lọc khách hàng đã hết hạn
                apiParams.expired_in = 0;
                apiParams.unit = 'week';
            } else if (filterType === 'custom' && expiryMonths) {
                // Lọc khách hàng hết hạn trong khoảng thời gian
                apiParams.expired_in = expiryMonths;
                apiParams.unit = expiryTimeUnit;
            }
            
            console.log('API Params:', apiParams);
            
            // Lấy dữ liệu từ API với filtering và pagination
            const response = await reqGetKMSOrganizationUsersActiveWithLicenseExpiredDate(apiParams);

            console.log('API Response:', response);

            // Kiểm tra response structure
            if (!response || !response.page || !Array.isArray(response.page)) {
                console.warn('Invalid API response structure:', response);
                setCustomers([]);
                setTotalCustomers(0);
                return;
            }

            // Convert API response thành CustomerData format
            const customerList: CustomerData[] = response.page.map((user: KMSUserWithLicenseExpired, index: number) => ({
                id: `user_${user.user_id}`,
                user_id: user.user_id,
                sign_in_provider: user.user_info?.sign_in_provider || 'unknown',
                phone_number: user.user_info?.phone_number || undefined,
                email: user.user_info?.email || undefined,
                external_user_id: user.user_info?.external_user_id || undefined,
                license_expired_date: user.license_expired_date,
                activation_count: 1,
                last_activation: Date.now() / 1000
            }));

            console.log('Mapped customerList:', customerList);

            setCustomers(customerList);
            // Sử dụng totalResult từ API response
            setTotalCustomers(response.totalResult);
            
            console.log('Debug pagination:', {
                customerCount: customerList.length,
                totalCustomers: response.totalResult,
                totalPage: response.totalPage,
                pageSize: size,
                currentPage: page
            });
        } catch (error) {
            console.error('Error fetching customers:', error);
            setCustomers([]);
            setTotalCustomers(0);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchCustomers(pageNumber, pageSize, sortField, sortOrder);
    }, [pageNumber, pageSize, sortField, sortOrder, expiryMonths, expiryTimeUnit, filterType]);

    // Export Excel using ExcelJS
    const exportToExcel = async () => {
        setLoading(true);
        try {
            // Gọi API để lấy tất cả dữ liệu khách hàng với size 999999
            const apiParams: {
                expired_in?: number;
                unit?: 'week' | 'month' | 'year';
                size: number;
                page: number;
            } = {
                size: 999999,
                page: 1
            };
            
            if (filterType === 'expired') {
                // Lọc khách hàng đã hết hạn
                apiParams.expired_in = 0;
                apiParams.unit = 'week';
            } else if (filterType === 'custom' && expiryMonths) {
                // Lọc khách hàng hết hạn trong khoảng thời gian
                apiParams.expired_in = expiryMonths;
                apiParams.unit = expiryTimeUnit;
            }
            
            const response = await reqGetKMSOrganizationUsersActiveWithLicenseExpiredDate(apiParams);
            
            if (!response || !response.page || !Array.isArray(response.page)) {
                return;
            }

            // Convert API response thành CustomerData format
            const allCustomers: CustomerData[] = response.page.map((user: KMSUserWithLicenseExpired, index: number) => ({
                id: `user_${user.user_id}`,
                user_id: user.user_id,
                sign_in_provider: user.user_info?.sign_in_provider || 'unknown',
                phone_number: user.user_info?.phone_number || undefined,
                email: user.user_info?.email || undefined,
                external_user_id: user.user_info?.external_user_id || undefined,
                license_expired_date: user.license_expired_date,
                activation_count: 1,
                last_activation: Date.now() / 1000
            }));

            if (allCustomers.length === 0) return;

            // Tạo workbook mới
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Danh sách khách hàng');

        // Định nghĩa columns với width lớn hơn
        worksheet.columns = [
            { header: 'STT', key: 'stt', width: 15 },
            { header: 'Thông tin liên hệ', key: 'contact', width: 40 },
            { header: 'Tình trạng License', key: 'status', width: 50 }
        ];

        // Style cho header - chỉ tô màu các cột được sử dụng
        worksheet.getRow(1).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
        worksheet.getRow(1).alignment = { 
            horizontal: 'center', 
            vertical: 'middle',
            wrapText: true 
        };
        worksheet.getRow(1).height = 30; // Tăng chiều cao header
        
        // Chỉ tô màu các cột được sử dụng (A, B, C)
        const headerCells = ['A1', 'B1', 'C1'];
        headerCells.forEach(cellAddress => {
            const cell = worksheet.getCell(cellAddress);
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF4472C4' }
            };
        });

            // Thêm dữ liệu
            allCustomers.forEach((customer, index) => {
            let contactInfo = '--';
            switch (customer.sign_in_provider) {
                case 'gofa':
                    contactInfo = `SĐT: ${customer.phone_number || '--'}`;
                    break;
                case 'google':
                    contactInfo = `Email: ${customer.email || '--'}`;
                    break;
                case 'apple':
                    contactInfo = `Apple ID: ${customer.external_user_id || '--'}`;
                    break;
            }

            const daysRemaining = getDaysRemaining(customer.license_expired_date);
            const proStatusText = !customer.license_expired_date ? 'Chưa kích hoạt' : 
                formatTimeRemaining(daysRemaining);
            
            const expiryDateText = customer.license_expired_date ? 
                new Date(parseInt(customer.license_expired_date) * 1000).toLocaleDateString('vi-VN') : '--';

            const row = worksheet.addRow({
                stt: index + 1,
                contact: contactInfo,
                status: `${proStatusText} - Ngày hết hạn: ${expiryDateText}`
            });

            // Style cho data rows
            row.font = { size: 11 };
            row.alignment = { 
                vertical: 'middle', 
                horizontal: 'left',
                wrapText: true 
            };
            row.height = 30; // Tăng chiều cao dòng dữ liệu
            
            // Căn giữa cột STT
            const sttCell = row.getCell(1); // Cột A (STT)
            sttCell.alignment = {
                vertical: 'middle',
                horizontal: 'center'
            };
        });

        // Áp dụng border cho tất cả cells
        worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
            row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FF000000' } },
                    left: { style: 'thin', color: { argb: 'FF000000' } },
                    bottom: { style: 'thin', color: { argb: 'FF000000' } },
                    right: { style: 'thin', color: { argb: 'FF000000' } }
                };
            });
        });

        // Header có border dày hơn
        worksheet.getRow(1).eachCell({ includeEmpty: false }, (cell) => {
            cell.border = {
                top: { style: 'medium', color: { argb: 'FF000000' } },
                left: { style: 'medium', color: { argb: 'FF000000' } },
                bottom: { style: 'medium', color: { argb: 'FF000000' } },
                right: { style: 'medium', color: { argb: 'FF000000' } }
            };
        });

            // Xuất file
            const currentDate = moment().format('DDMMYYYY');
            const fileName = `${currentDate}_danhsach_khachhang.xlsx`;
            
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting customers:', error);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'STT',
            key: 'index',
            render: (_: any, __: any, index: number) => (
                <span className="text-black font-medium">{(pageNumber - 1) * pageSize + index + 1}</span>
            ),
            width: '8%',
            align: 'center' as const,
        },
        {
            title: 'Thông tin liên hệ',
            key: 'contact_info',
            render: (text: any, record: CustomerData) => {
                let displayLabel = "";
                let displayValue = "";

                switch (record.sign_in_provider) {
                    case 'gofa':
                        displayLabel = "SĐT:";
                        displayValue = record.phone_number || '--';
                        break;
                    case 'google':
                        displayLabel = "Email:";
                        displayValue = record.email || '--';
                        break;
                    case 'apple':
                        displayLabel = "Apple ID:";
                        displayValue = record.external_user_id || '--';
                        break;
                    default:
                        displayLabel = "Info:";
                        displayValue = '--';
                }

                const fullText = `${displayLabel} ${displayValue}`;

                return (
                    <div className="flex items-center justify-start text-black w-full pl-4">
                        <div className="flex items-center text-sm w-full">
                            <Tooltip title="Xem thêm" placement="top">
                                <div 
                                    className="flex items-center cursor-pointer w-full rounded px-2 py-1"
                                    onClick={() => showInfoModal('Thông tin liên hệ', fullText)}
                                >
                                    <span className="font-medium mr-1 flex-shrink-0">{displayLabel}</span>
                                    <span className="text-slate-500 truncate flex-1 min-w-0">
                                        {displayValue}
                                    </span>
                                </div>
                            </Tooltip>
                        </div>
                    </div>
                );
            },
            width: '35%',
            align: 'left' as const,
        },
        {
            title: 'Tình trạng License',
            key: 'pro_status',
            render: (text: any, record: CustomerData) => {
                if (!record.license_expired_date) {
                    return <div className="text-left text-sm text-black font-medium pl-4">Chưa kích hoạt</div>;
                }

                const daysRemaining = getDaysRemaining(record.license_expired_date);
                const isExpiringSoon = daysRemaining <= 30;
                const isExpired = daysRemaining <= 0;

                const expiryDate = new Date(parseInt(record.license_expired_date) * 1000).toLocaleDateString('vi-VN');
                const timeRemaining = formatTimeRemaining(daysRemaining);
                const fullStatusText = `Ngày hết hạn: ${expiryDate} (${timeRemaining})`;

                return (
                    <div className="flex items-center justify-start text-black w-full pl-4">
                        <Tooltip title="Xem thêm" placement="top">
                            <div 
                                className={`inline-flex items-center justify-start px-3 py-1.5 font-medium text-sm min-w-[200px] w-full cursor-pointer rounded ${
                                    isExpired 
                                        ? 'text-red-600'
                                        : isExpiringSoon 
                                        ? 'text-amber-600'
                                        : 'text-emerald-600'
                                }`}
                                onClick={() => showInfoModal('Tình trạng License', fullStatusText)}
                            >
                                <span className="truncate flex-1 min-w-0">
                                    {fullStatusText}
                                </span>
                            </div>
                        </Tooltip>
                    </div>
                );
            },
            width: '57%',
            align: 'left' as const,
        },
    ];

    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
        if (sorter.field) {
            setSortField(sorter.field);
            setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        } else {
            setSortField('');
            setSortOrder('desc');
        }
    };

    return (
        <div className="h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 overflow-hidden">
            <div className='h-full flex flex-col px-4 sm:px-6 pt-1 pb-2'>
                {/* User Profile Header */}
                <div className="flex justify-end mb-1">
                    <UserProfile />
                </div>

                {/* Table Section */}
                <div className='flex-1 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden flex flex-col min-h-0'>
                    <div className='p-2 sm:p-3 border-b border-gray-100'>
                        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2'>
                            <h2 className='text-base sm:text-lg font-bold text-gray-900'>Quản lý khách hàng</h2>
                            <Button 
                                type="primary" 
                                onClick={exportToExcel}
                                disabled={customers.length === 0}
                                className='bg-blue-600 border-blue-600 w-full sm:w-auto'
                            >
                                Xuất Excel
                            </Button>
                        </div>
                    </div>

                    {/* Filter Controls and Pagination */}
                    <div className='flex-shrink-0 border-b border-gray-100 bg-white px-3 sm:px-4 py-3 shadow-sm'>
                        <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4'>
                            {/* Filters Section - Left */}
                            <div className='flex flex-col sm:flex-row gap-3'>
                                <div className='flex items-center gap-2'>
                                    <span className='text-sm font-medium text-gray-700'>Bộ lọc:</span>
                                    <Select
                                        value={filterType}
                                        onChange={(value) => {
                                            setFilterType(value);
                                            if (value !== 'custom') {
                                                setExpiryMonths(null);
                                            }
                                            setPageNumber(1);
                                        }}
                                        className='w-full sm:w-[180px]'
                                    >
                                        <Option value="all">Tất cả khách hàng</Option>
                                        <Option value="expired">Khách hàng hết hạn</Option>
                                        <Option value="custom">Hết hạn trong khoảng</Option>
                                    </Select>
                                </div>

                                {filterType === 'custom' && (
                                    <div className='flex items-center gap-2'>
                                        <Input
                                            type="number"
                                            placeholder="Nhập số"
                                            value={expiryMonths || ''}
                                            onChange={(e) => {
                                                const value = e.target.value ? parseInt(e.target.value) : null;
                                                setExpiryMonths(value);
                                                setPageNumber(1);
                                            }}
                                            style={{ width: 100 }}
                                            min={1}
                                            max={1000}
                                        />
                                        <Select
                                            value={expiryTimeUnit}
                                            onChange={(value) => {
                                                setExpiryTimeUnit(value);
                                                setPageNumber(1);
                                            }}
                                            style={{ width: 120 }}
                                        >
                                            <Option value="week">tuần nữa</Option>
                                            <Option value="month">tháng nữa</Option>
                                            <Option value="year">năm nữa</Option>
                                        </Select>
                                    </div>
                                )}

                            </div>
                            
                            {/* Pagination Section - Right */}
                            <div className='flex flex-col sm:flex-row items-start sm:items-center gap-3'>
                                <div className='flex items-center gap-2'>
                                    <span className='text-sm text-gray-700'>Số mục/trang:</span>
                                    <Select
                                        value={pageSize}
                                        onChange={(value) => {
                                            setPageSize(value);
                                            setPageNumber(1);
                                        }}
                                        className='w-24'
                                        size='small'
                                    >
                                        <Option value={10}>10</Option>
                                        <Option value={20}>20</Option>
                                        <Option value={50}>50</Option>
                                        <Option value={100}>100</Option>
                                    </Select>
                                </div>
                                <div className='flex items-center gap-1'>
                                    <Button
                                        disabled={pageNumber <= 1}
                                        onClick={() => {
                                            const newPage = pageNumber - 1;
                                            setPageNumber(newPage);
                                            fetchCustomers(newPage, pageSize);
                                        }}
                                        size='small'
                                        type='default'
                                        className='px-2'
                                    >
                                        ‹
                                    </Button>

                                    {/* Smart Page Numbers */}
                                    <div className='hidden sm:flex items-center gap-1'>
                                        {(() => {
                                            const totalPages = Math.ceil(totalCustomers / pageSize);
                                            const pages = [];
                                            const showPages = 5;

                                            if (totalPages <= showPages) {
                                                for (let i = 1; i <= totalPages; i++) {
                                                    pages.push(i);
                                                }
                                            } else {
                                                const startPage = Math.max(1, pageNumber - 1);
                                                const endPage = Math.min(totalPages, pageNumber + 1);

                                                if (startPage > 1) {
                                                    pages.push(1);
                                                    if (startPage > 2) {
                                                        pages.push('...');
                                                    }
                                                }

                                                for (let i = startPage; i <= endPage; i++) {
                                                    pages.push(i);
                                                }

                                                if (endPage < totalPages) {
                                                    if (endPage < totalPages - 1) {
                                                        pages.push('...');
                                                    }
                                                    pages.push(totalPages);
                                                }
                                            }

                                            return pages.map((page, index) => {
                                                if (page === '...') {
                                                    return (
                                                        <span key={`ellipsis-${index}`} className='px-1 py-1 text-gray-400 text-sm'>
                                                            ...
                                                        </span>
                                                    );
                                                }

                                                const isActive = page === pageNumber;
                                                return (
                                                    <Button
                                                        key={page}
                                                        type={isActive ? 'primary' : 'text'}
                                                        size='small'
                                                        className={`w-7 h-7 p-0 text-xs ${isActive ? 'bg-blue-600' : ''}`}
                                                        onClick={() => {
                                                            setPageNumber(page as number);
                                                            fetchCustomers(page as number, pageSize);
                                                        }}
                                                    >
                                                        {page}
                                                    </Button>
                                                );
                                            });
                                        })()}
                                    </div>

                                    {/* Mobile: Show current page input */}
                                    <div className='flex sm:hidden items-center gap-1'>
                                        <Input
                                            value={pageNumber}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value);
                                                const totalPages = Math.ceil(totalCustomers / pageSize);
                                                if (value > 0 && value <= totalPages) {
                                                    setPageNumber(value);
                                                }
                                            }}
                                            onPressEnter={(e) => {
                                                const value = parseInt((e.target as HTMLInputElement).value);
                                                const totalPages = Math.ceil(totalCustomers / pageSize);
                                                if (value > 0 && value <= totalPages) {
                                                    fetchCustomers(value, pageSize);
                                                }
                                            }}
                                            className='w-10 text-center'
                                            size='small'
                                        />
                                        <span className='text-xs text-gray-500'>/ {Math.ceil(totalCustomers / pageSize)}</span>
                                    </div>

                                    <Button
                                        disabled={pageNumber >= Math.ceil(totalCustomers / pageSize)}
                                        onClick={() => {
                                            const newPage = pageNumber + 1;
                                            setPageNumber(newPage);
                                            fetchCustomers(newPage, pageSize);
                                        }}
                                        size='small'
                                        type='default'
                                        className='px-2'
                                    >
                                        ›
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='flex-1 overflow-hidden flex flex-col'>
                        <div className='flex-1 overflow-auto'>
                            <Table
                                columns={columns}
                                dataSource={customers}
                                rowKey="id"
                                loading={loading}
                                pagination={false}
                                onChange={handleTableChange}
                                size="small"
                                className="border-0"
                                scroll={{ x: 'max-content' }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Loading overlay */}
            {loading && (
                <>
                    <div className="fixed inset-0 z-40 bg-black bg-opacity-10 pointer-events-none" />
                    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
                        <div className="flex items-center gap-3 text-gray-700 bg-white bg-opacity-90 rounded-lg px-6 py-4 shadow-lg">
                            <Spin size="default" />
                            <span className="text-base font-medium">Đang tải dữ liệu...</span>
                        </div>
                    </div>
                </>
            )}

            {/* Info Modal */}
            <Modal
                title={modalContent.title}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setModalVisible(false)}>
                        Đóng
                    </Button>
                ]}
                width={500}
                centered
            >
                <div className="py-4">
                    <p className="text-base text-gray-800 break-words whitespace-pre-wrap">
                        {modalContent.content}
                    </p>
                </div>
            </Modal>
        </div>
    );
};

export default CustomerManagement;
